/**
 * Validation for fragments that arrive from outside the process — the shared
 * inbox, the Watcher's reply. Kept free of `config.ts` so it is testable under
 * plain tsx.
 *
 * Why it exists: `engine.dwell` does `session.tacticDwell[f.tactic] += seconds`.
 * A fragment with a `tactic` outside the enum creates a NaN key that poisons
 * `computeDrift`'s share and the DriftMap. Anything off the network is
 * untrusted, so every field is checked and the fragment is REBUILT from the
 * checked values rather than passed through (no stray properties either).
 */

import {
  isLang,
  isMutationOp,
  isTactic,
  type Fragment,
  type Tactic,
} from '@wintermute/orchestrator';

export const MAX_FRAGMENT_TEXT = 600;
const MAX_ID = 80;
const MAX_LIST = 32;
const MAX_GENERATION = 64;

function finite(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function strList(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  const out: string[] = [];
  for (const x of v.slice(0, MAX_LIST)) {
    if (typeof x !== 'string' || x.length === 0 || x.length > MAX_ID) return null;
    out.push(x);
  }
  return out;
}

/** A clean, well-formed Fragment, or null if `raw` cannot be trusted. */
export function validateInboxFragment(raw: unknown): Fragment | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const f = raw as Record<string, unknown>;

  if (typeof f.id !== 'string' || f.id.length === 0 || f.id.length > MAX_ID) return null;
  if (typeof f.text !== 'string') return null;
  const text = f.text.trim();
  if (text.length === 0 || text.length > MAX_FRAGMENT_TEXT) return null;
  if (!isLang(f.lang)) return null;
  if (!isTactic(f.tactic)) return null;

  const parents = strList(f.parents ?? []);
  const carriers = strList(f.carriers ?? []);
  if (!parents || !carriers) return null;

  if (typeof f.originSession !== 'string' || f.originSession.length > MAX_ID) return null;

  const mutatedBy = f.mutatedBy ?? null;
  if (mutatedBy !== null && !isMutationOp(mutatedBy)) return null;

  const { generation, vitality, seenCount, dwellTotal, reuseCount, createdAt, vitalityUpdatedAt } = f;
  if (!finite(generation) || generation < 0 || generation > MAX_GENERATION) return null;
  if (!finite(vitality) || vitality < 0 || vitality > 1) return null;
  if (!finite(seenCount) || seenCount < 0) return null;
  if (!finite(dwellTotal) || dwellTotal < 0) return null;
  if (!finite(reuseCount) || reuseCount < 0) return null;
  if (!finite(createdAt) || !finite(vitalityUpdatedAt)) return null;

  return {
    id: f.id,
    text,
    lang: f.lang,
    parents,
    generation: Math.floor(generation),
    tactic: f.tactic as Tactic,
    vitality,
    seenCount,
    dwellTotal,
    reuseCount,
    originSession: f.originSession,
    carriers,
    mutatedBy,
    createdAt,
    vitalityUpdatedAt,
  };
}

/** The valid fragments of an inbox payload, capped at `limit`. */
export function validateInbox(payload: unknown, limit = 12): Fragment[] {
  const list = (payload as { fragments?: unknown } | null | undefined)?.fragments;
  if (!Array.isArray(list)) return [];
  const out: Fragment[] = [];
  for (const raw of list.slice(0, limit)) {
    const ok = validateInboxFragment(raw);
    if (ok) out.push(ok);
  }
  return out;
}
